// Workspace Imports
import { model } from "GenAlphaModel";
// Local Imports
import { type DrawTo, MAX_PIXEL_AREA } from "DrawCore/src/draw/drawto";
import type { HtmlCanvas } from "./root";
import { DrawToHtmlCanvas } from "./drawto";
import { ViewPort } from "./viewport";

export class Sprite {
  private readonly canvas: HTMLCanvasElement = document.createElement("canvas");
  private readonly ctx: CanvasRenderingContext2D = this.canvas.getContext(
    "2d",
    { willReadFrequently: true }
  )!;
  readonly size: model.Size;
  private renderedScale!: number;

  constructor(
    readonly root: HtmlCanvas,
    readonly bbox: model.Bbox,
    readonly fn: (to: DrawTo) => unknown,
    targetScale: number
  ) {
    this.size = model.Bbox.size(bbox);
    this.render(targetScale);
  }

  // Render the sprite to our private canvas
  private render(targetScale: number): void {
    this.renderedScale = targetScale;
    const { size, bbox, fn } = this;

    // Calculate canvas size. Ensuring min pixel dimensions, or you get some "error zero size canvas"s.
    let canvasWidth = Math.max(100, Math.round(size.width * targetScale));
    let canvasHeight = Math.max(100, Math.round(size.height * targetScale));

    // Clamp total pixel area to avoid exceeding browser canvas limits.
    // Scale both dimensions down proportionally to fit within the budget.
    const pixelArea = canvasWidth * canvasHeight;
    if (pixelArea > MAX_PIXEL_AREA) {
      const scaleFactor = Math.sqrt(MAX_PIXEL_AREA / pixelArea);
      canvasWidth = Math.max(100, Math.round(canvasWidth * scaleFactor));
      canvasHeight = Math.max(100, Math.round(canvasHeight * scaleFactor));
    }

    this.canvas.width = canvasWidth;
    this.canvas.height = canvasHeight;

    // Calculate independent X and Y scale factors so the canvas pixel space
    // maps exactly to world space in both dimensions. This is critical because
    // the Math.max(100, ...) clamp above may change the canvas aspect ratio
    // relative to the bbox. Using independent scales ensures drawImage()
    // (which stretches the canvas back to size.width × size.height) produces
    // correct proportions.
    const scaleX = this.canvas.width / size.width;
    const scaleY = this.canvas.height / size.height;

    // Now we ready to set up for drawing
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.save();
    this.ctx.scale(scaleX, scaleY);
    this.ctx.translate(-bbox.min.x, -bbox.min.y);

    // Do the actual drawing in our target function
    // Note this uses an infinite viewport, or we'd need to figure out different viewports per instance of this sprite.
    // The "sprite" bucket tag flows through this DrawTo so every primitive
    // drawn while rasterizing the sprite is attributed to cache-miss work.
    const spriteDrawTo = new DrawToHtmlCanvas(
      this.root,
      this.ctx,
      ViewPort.infinite(),
      "sprite"
    );
    fn(spriteDrawTo);

    // And restore the context, we guess? It probably doesn't matter, we're done with it.
    this.ctx.restore();
  }

  // Check if the sprite needs to be re-rendered
  needsRerender(targetScale: number): boolean {
    // If the scale has changed by 2x or more since last render
    return (
      targetScale > 2 * this.renderedScale ||
      targetScale < this.renderedScale / 2
    );
  }

  // Draw this sprite to a target canvas context
  // Note this scales to `this.size` per the arguments to `drawImage`.
  drawTo(targetCtx: CanvasRenderingContext2D, targetScale: number): void {
    // Check if existing sprite needs re-rendering due to scale change
    if (this.needsRerender(targetScale)) {
      this.render(targetScale);
    }

    const { size, bbox } = this;
    targetCtx.drawImage(
      this.canvas,
      bbox.min.x,
      bbox.min.y,
      size.width,
      size.height
    );
  }
}

// Sprite cache key type
// Uses object equality, i.e. identity,
// for the kinds of things we generally want as cache keys.
// Layouts are the prominent example.
export type Key = object;

export class SpriteCache {
  //
  // The main thing: the inner WeakMap
  private inner = new WeakMap<Key, Sprite>();

  // Performance counters
  private hits: number = 0;
  private misses: number = 0;
  private total: number = 0;

  get(key: Key): Sprite | undefined {
    this.total += 1;
    const sprite = this.inner.get(key);
    if (sprite) {
      this.hits += 1;
      return sprite;
    } else {
      this.misses += 1;
      return undefined;
    }
  }

  set(key: Key, sprite: Sprite): void {
    this.inner.set(key, sprite);
  }

  // Clear entire cache (useful for global invalidation events)
  clear(): void {
    this.inner = new WeakMap<Key, Sprite>();
    this.hits = 0;
    this.misses = 0;
    this.total = 0;
  }
}
